// BillingItem.jsx
import React, { useState, useRef } from "react";
import { Table, Button, Icon, Confirm } from "semantic-ui-react";
import { BasicModal } from "../../../Shared";
import { BillingForm } from "../BillingForm";
import { useAuth } from "../../../../hooks";
import { BillingsAPI } from "../../../../api/billingsAPI";
import { User } from "../../../../api"; // API para listar usuarios; contiene getUserByAdmin
import { PlanesAPI } from "../../../../api/planesAPI"; // Para obtener detalle de cada plan y para remover activaciones
import "./BillingItem.scss";

const billingController = new BillingsAPI();
// Usamos la clase User para obtener al partner mediante getUserByAdmin
const userAPI = new User();
// Definimos la instancia para las operaciones de activaciones:
const planActivationsAPI = new PlanesAPI();

export function BillingItem(props) {
  const { billing, onReload } = props;
  const { accessToken, user } = useAuth();

  // adminId se obtiene del usuario logueado (se espera que sea admin)
  const adminId = user.user._id;

  // billingObj contiene los datos de la factura y su usuario asociado (partner)
  const billingObj = billing.billings || {};
  const partnerFromBilling = billingObj.user || {};

  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Estado para el modal de activaciones
  const [showActivationsModal, setShowActivationsModal] = useState(false);
  // Estado para almacenar las activaciones con detalles (plan title y quantity)
  const [partnerActivations, setPartnerActivations] = useState([]);

  // Referencias a inputs de archivo (ocultos)
  const fileInputClientRef = useRef(null);
  const fileInputProviderRef = useRef(null);

  const onOpenCloseModal = () => setShowModal((prev) => !prev);
  const onOpenCloseConfirm = () => setShowConfirm((prev) => !prev);
  const onOpenCloseActivationsModal = () =>
    setShowActivationsModal((prev) => !prev);

  // Función para eliminar la factura
  const onDelete = async () => {
    try {
      await billingController.deleteBilling(accessToken, billing._id);
      onReload();
      onOpenCloseConfirm();
    } catch (error) {
      console.error("Error eliminando factura:", error);
    }
  };

  // Función para descargar un archivo (abre la URL en una nueva pestaña)
  const handleDownload = (url) => {
    window.open(url, "_blank");
  };

  // SUBIR ARCHIVOS: Factura Cliente
  const handleClickUploadClient = () => {
    fileInputClientRef.current.click();
  };

  const handleFileChangeClient = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await billingController.updateBillingFile(
        accessToken,
        billing._id,
        true,
        file,
      );
      onReload();
    } catch (err) {
      console.error(err);
    }
  };

  // SUBIR ARCHIVOS: Factura Proveedor
  const handleClickUploadProvider = () => {
    fileInputProviderRef.current.click();
  };

  const handleFileChangeProvider = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await billingController.updateBillingFile(
        accessToken,
        billing._id,
        false,
        file,
      );
      onReload();
    } catch (err) {
      console.error(err);
    }
  };

  // Render Factura Cliente
  const renderFacturaCliente = () => {
    if (!billingObj.billingdata) {
      return (
        <>
          <Button
            size="mini"
            onClick={handleClickUploadClient}
            title="Subir Factura Cliente"
          >
            <Icon name="upload" /> Subir
          </Button>
          <input
            type="file"
            ref={fileInputClientRef}
            style={{ display: "none" }}
            onChange={handleFileChangeClient}
            accept="application/pdf,image/*"
          />
        </>
      );
    } else {
      return (
        <>
          <Button
            size="mini"
            onClick={() => handleDownload(billingObj.billingdata)}
            title="Descargar Factura Cliente"
          >
            <Icon name="download" /> Descargar
          </Button>
          <Button
            size="mini"
            onClick={handleClickUploadClient}
            title="Subir nueva Factura Cliente"
          >
            <Icon name="upload" /> Subir
          </Button>
          <input
            type="file"
            ref={fileInputClientRef}
            style={{ display: "none" }}
            onChange={handleFileChangeClient}
            accept="application/pdf,image/*"
          />
        </>
      );
    }
  };

  // Render Factura Proveedor
  const renderFacturaProveedor = () => {
    if (!billingObj.billingdatapartner) {
      return (
        <>
          <Button
            size="mini"
            onClick={handleClickUploadProvider}
            title="Subir Factura Proveedor"
          >
            <Icon name="upload" /> Subir
          </Button>
          <input
            type="file"
            ref={fileInputProviderRef}
            style={{ display: "none" }}
            onChange={handleFileChangeProvider}
            accept="application/pdf,image/*"
          />
        </>
      );
    } else {
      return (
        <>
          <Button
            size="mini"
            onClick={() => handleDownload(billingObj.billingdatapartner)}
            title="Descargar Factura Proveedor"
          >
            <Icon name="download" /> Descargar
          </Button>
          <Button
            size="mini"
            onClick={handleClickUploadProvider}
            title="Subir nueva Factura Proveedor"
          >
            <Icon name="upload" /> Subir
          </Button>
          <input
            type="file"
            ref={fileInputProviderRef}
            style={{ display: "none" }}
            onChange={handleFileChangeProvider}
            accept="application/pdf,image/*"
          />
        </>
      );
    }
  };

  const dateFormatted = new Date(billingObj.created_at).toLocaleDateString(
    "es-ES",
    {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
  );

  // Función para obtener el partner mediante getUserByAdmin y sus activaciones
  const handleOpenActivationsModal = async () => {
    try {
      // partnerId se obtiene de billingObj.user._id
      const partnerId = billingObj.user._id;
      const partnerData = await userAPI.getUserByAdmin(
        accessToken,
        adminId,
        partnerId,
      );
      if (!partnerData || !partnerData.user) {
        alert("No se pudo obtener la información del partner.");
        return;
      }
      const activations = partnerData.user.planEducaActivations || [];
      // Para cada activación, obtener detalle del plan usando PlanesAPI.getPlanById
      const activationDetailsPromises = activations.map(async (act) => {
        try {
          const planDetail = await planActivationsAPI.getPlanById(
            accessToken,
            act.plan,
          );
          return {
            planId: act.plan,
            planTitle: planDetail.plan ? planDetail.plan.title : act.plan,
            quantity: act.quantity,
          };
        } catch (error) {
          console.error("Error obteniendo plan", act.plan, error);
          return {
            planId: act.plan,
            planTitle: act.plan,
            quantity: act.quantity,
          };
        }
      });
      const activationsWithDetails = await Promise.all(
        activationDetailsPromises,
      );
      setPartnerActivations(activationsWithDetails);
      setShowActivationsModal(true);
    } catch (error) {
      console.error("Error al obtener activaciones del partner:", error);
      alert("Error al obtener activaciones.");
    }
  };

  // Render del contenido del modal de activaciones: se muestra en una tabla con un botón de borrar para cada fila.
  const renderActivationsModalContent = () => (
    <div className="activations-modal-content">
      <h3>Activaciones de Planes</h3>
      {partnerActivations && partnerActivations.length > 0 ? (
        <table className="activations-table">
          <thead>
            <tr>
              <th>Plan</th>
              <th>Cantidad</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {partnerActivations.map((act, index) => (
              <tr key={index}>
                <td>{act.planTitle}</td>
                <td>{act.quantity}</td>
                <td>
                  <Button
                    icon
                    color="red"
                    size="mini"
                    onClick={() =>
                      handleRemoveActivation(act.planId, act.quantity)
                    }
                    title="Eliminar activación"
                  >
                    <Icon name="trash" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No hay activaciones registradas.</p>
      )}
    </div>
  );

  // Función para eliminar activación: llama a removeActivations y actualiza la lista
  const handleRemoveActivation = async (planId, quantity) => {
    try {
      const partnerId = billingObj.user._id;
      const data = { activations: [{ plan: planId, quantity }] };
      await planActivationsAPI.removeActivations(
        accessToken,
        adminId,
        partnerId,
        data,
      );
      alert("Activación eliminada correctamente.");
      await handleOpenActivationsModal();
    } catch (error) {
      console.error("Error al remover activación:", error);
      alert("Error al remover activación.");
    }
  };

  return (
    <>
      <Table.Row>
        <Table.Cell>{partnerFromBilling.role || "N/A"}</Table.Cell>
        <Table.Cell>{`${partnerFromBilling.firstname || ""} ${partnerFromBilling.lastname || ""}`}</Table.Cell>
        <Table.Cell>{billingObj.billingNumber}</Table.Cell>
        <Table.Cell>{billingObj.description}</Table.Cell>
        <Table.Cell>{billingObj.billingtotal}</Table.Cell>
        <Table.Cell>{billingObj.billingpartner}</Table.Cell>
        <Table.Cell>{billingObj.status}</Table.Cell>
        <Table.Cell>{renderFacturaCliente()}</Table.Cell>
        <Table.Cell>{renderFacturaProveedor()}</Table.Cell>
        <Table.Cell>{dateFormatted}</Table.Cell>
        <Table.Cell>
          <Button
            icon
            primary
            size="mini"
            onClick={onOpenCloseModal}
            title="Editar Factura"
          >
            <Icon name="pencil" />
          </Button>
          {/* Botón "eye" para ver activaciones */}
          <Button
            icon
            secondary
            size="mini"
            onClick={handleOpenActivationsModal}
            title="Ver Activaciones"
          >
            <Icon name="eye" />
          </Button>
          <Button
            icon
            color="red"
            size="mini"
            onClick={onOpenCloseConfirm}
            title="Eliminar Factura"
          >
            <Icon name="trash" />
          </Button>
        </Table.Cell>
      </Table.Row>

      {/* Modal para editar factura */}
      <BasicModal
        show={showModal}
        close={onOpenCloseModal}
        title="Editar Factura"
        size="large"
      >
        <BillingForm
          billing={billing}
          onClose={onOpenCloseModal}
          onReload={onReload}
          accessToken={accessToken}
        />
      </BasicModal>

      {/* Modal para ver activaciones */}
      <BasicModal
        show={showActivationsModal}
        close={onOpenCloseActivationsModal}
        title="Activaciones del Usuario Partner"
        size="small"
      >
        {renderActivationsModalContent()}
      </BasicModal>

      {/* Confirm de eliminación */}
      <Confirm
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={onDelete}
        content={`¿Estás seguro de eliminar la factura #${billingObj.billingNumber}?`}
        size="mini"
      />
    </>
  );
}
